import React, { useCallback, useEffect, useState } from 'react';
import { roleLabel } from './roleLabel';

// "just now" / "5m ago" / "3h ago" / "2d ago"; null past 30 days (the date says it).
export function formatLastAccess(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const sec = Math.max(0, (now - t) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 30 * 86400) return `${Math.floor(sec / 86400)}d ago`;
  return null;
}

// Always UTC so every operator reads the same time.
export function formatAccessTime(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    timeZone: 'UTC', timeZoneName: 'short',
  });
}

/**
 * UsersPanel — manage aveq-agent CHAT USERS and their access
 * (aveq-admin-user-access slug). Distinct from the Permissions tab,
 * which manages aveq-admin OPERATORS.
 *
 * Proxied to aveq-agent /admin/access/* via /api/user-access/* (those
 * responses are aveq-agent's raw shape — {users:[…]}, {roles:[…]} — not
 * the hub's {entity} wrapper).
 *
 * Capabilities (per the slug requirements):
 *   - View users + assigned roles; add/remove a user's roles.
 *   - Revoke a user temporarily (with an until date) or permanently;
 *     reinstate.
 *   - View roles; revoke / reinstate / delete a role.
 *   - See and change a user's station pass (how many demo stations they can build).
 *   - Create a user (sign-up is by invitation since public-homepage).
 * No role creation here — roles
 * are minted by the Protect action on assets (or the access_ctl CLI).
 *
 * Gated at App.js by `manage_user_access @ cluster`; the BFF re-gates,
 * so direct curl still 403s without the permission.
 */
export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // passkey-only-accounts: a restore's one-time password, shown until the operator closes it.
  const [oneTime, setOneTime] = useState(null);
  const [assigningTo, setAssigningTo] = useState(null);   // user object or null
  const [revoking, setRevoking] = useState(null);         // user object or null
  const [passFor, setPassFor] = useState(null);           // user object or null
  const [passDefault, setPassDefault] = useState(null);   // STATION_PASS_LIMIT on the agent
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, r] = await Promise.all([
        fetch('/api/user-access/users', { credentials: 'same-origin' }),
        fetch('/api/user-access/roles', { credentials: 'same-origin' }),
      ]);
      if (!u.ok) { setError(`/api/user-access/users → HTTP ${u.status}`); return; }
      if (!r.ok) { setError(`/api/user-access/roles → HTTP ${r.status}`); return; }
      const ub = (await u.json()) || {};
      setUsers(ub.users || []);
      setPassDefault(ub.station_pass_default ?? null);
      setRoles(((await r.json()) || {}).roles || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const call = async (url, method) => {
    const r = await fetch(url, { method, credentials: 'same-origin' });
    if (!r.ok) { window.alert(`${method} ${url} → HTTP ${r.status}\n${await r.text()}`); return false; }
    return true;
  };

  const removeRole = async (u, role) => {
    if (!window.confirm(`Remove role "${roleLabel(role.name)}" from ${u.email || u.account_id}?`)) return;
    if (await call(`/api/user-access/accounts/${u.account_id}/roles/${role.role_id}`, 'DELETE')) refresh();
  };

  const reinstateUser = async (u) => {
    if (await call(`/api/user-access/accounts/${u.account_id}/reinstate`, 'POST')) refresh();
  };

  // passkey-only-accounts: recovery when the owner lost every passkey. The agent sets a
  // one-time password and returns it once; the operator passes it on.
  const restorePassword = async (u) => {
    if (!window.confirm(
      `Restore password sign-in for ${u.email || u.account_id}?\n\n` +
      `They turned it off to sign in with passkeys only. Do this only once you're sure the ` +
      `request comes from the account's owner.\n\n` +
      `Their old password stays off: you'll get a one-time password to give them, and they ` +
      `choose a new one when they sign in. Their sessions are ended.`)) return;
    const url = `/api/user-access/accounts/${u.account_id}/restore-password`;
    const r = await fetch(url, { method: 'POST', credentials: 'same-origin' });
    if (!r.ok) { window.alert(`POST ${url} → HTTP ${r.status}\n${await r.text()}`); return; }
    const { temporary_password: password } = await r.json();
    setOneTime({ who: u.email || `account ${u.account_id}`, password });
    refresh();
  };

  const revokeRole = async (role) => {
    if (await call(`/api/user-access/roles/${role.id}/revoke`, 'POST')) refresh();
  };
  const reinstateRole = async (role) => {
    if (await call(`/api/user-access/roles/${role.id}/reinstate`, 'POST')) refresh();
  };
  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${roleLabel(role.name)}"? This drops its grants + assignments.`)) return;
    if (await call(`/api/user-access/roles/${role.id}`, 'DELETE')) refresh();
  };
  const purgeAccount = async (u) => {
    if (!window.confirm(
      `Permanently PURGE account ${u.account_id}${u.email ? ` (${u.email})` : ''}?\n\n` +
      `Hard-deletes the account and ALL its data — credentials, profile, sessions, ` +
      `scheduled tasks, notifications, API keys/tokens. There is no undo.\n\n` +
      `(The account is already revoked; purge is the irreversible erase.)`)) return;
    if (await call(`/api/user-access/accounts/${u.account_id}/purge`, 'POST')) refresh();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Users</h2>
        <button onClick={refresh} style={refreshBtnStyle}>refresh</button>
      </div>
      {oneTime && (
        <div role="status" data-testid="one-time-password" style={{
          margin: '8px 0 16px', padding: '12px 14px', border: '1px solid #c9a94a', borderRadius: 6,
          background: '#fff8e1', fontSize: '0.85rem', maxWidth: 520,
        }}>
          One-time password for <strong>{oneTime.who}</strong>:{' '}
          <code style={{ fontSize: '1rem', padding: '2px 6px', background: '#fff', border: '1px solid #eee' }}>
            {oneTime.password}
          </code>
          <div style={{ color: '#7a6420', marginTop: 6 }}>
            Shown once — give it to them privately. They'll choose a new password when they sign in.
          </div>
          <button onClick={() => setOneTime(null)} style={{ ...smallBtnStyle, marginTop: 8 }}>done</button>
        </div>
      )}
      <div style={{ color: '#888', fontSize: '0.78rem', marginBottom: 16 }}>
        Chat users (aveq-agent) and their access roles — not aveq-admin operators.
      </div>

      {loading && <div style={{ color: '#888' }}>Loading…</div>}
      {error && <div style={{ color: '#c00' }}>{error}</div>}

      {!loading && !error && (
        <>
          <Section title="Chat users" count={users.length}
            action={<button onClick={() => setCreating(true)} style={smallBtnStyle}>+ create user</button>}>
            {users.length === 0 ? (
              <Empty>No chat users yet. Create one with “+ create user”.</Empty>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={trHeadStyle}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle} title="Last sign-in or signed-in request (UTC). An open tab can count as access.">Last access</th>
                    <th style={thStyle}>Roles</th>
                    <th style={thStyle}>Station pass</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.account_id} style={trStyle}>
                      <td style={tdStyle}>{u.account_id}</td>
                      <td style={tdStyle}>{u.email || <em style={{ color: '#999' }}>—</em>}</td>
                      <td style={tdStyle}>{u.display_name || <em style={{ color: '#999' }}>—</em>}</td>
                      <td style={tdStyle}>
                        <RevokedBadge revoked={u.revoked} />
                        {u.passkey_only && (
                          <span style={{ ...pillStyle, background: '#e8eef8', color: '#345', marginLeft: 6 }}
                                title="Password sign-in is off; this account signs in with a passkey">passkey only</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={`last-access-${u.account_id}`}>
                        {u.last_access_at ? <LastAccess iso={u.last_access_at} />
                          : <em style={{ color: '#999' }}>never</em>}
                      </td>
                      <td style={tdStyle}>
                        {(!u.roles || u.roles.length === 0) ? (
                          <em style={{ color: '#999' }}>none</em>
                        ) : (
                          u.roles.map((role) => (
                            <span key={role.role_id} style={{ marginRight: 4, whiteSpace: 'nowrap' }}>
                              <span style={{ ...badgeStyle, opacity: role.revoked ? 0.45 : 1 }}
                                title={role.revoked ? 'role is revoked (inert)' : ''}>
                                {roleLabel(role.name)}{role.revoked ? ' (revoked)' : ''}
                              </span>
                              <button title={`Remove ${roleLabel(role.name)}`} style={revokeBtnStyle}
                                onClick={() => removeRole(u, role)}>×</button>
                            </span>
                          ))
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={`pass-${u.account_id}`}>
                        <PassCell pass={u.station_pass} />
                        <button onClick={() => setPassFor(u)} style={{ ...smallBtnStyle, marginLeft: 6 }}
                                data-testid={`pass-edit-${u.account_id}`}>edit</button>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <button onClick={() => setAssigningTo(u)} style={smallBtnStyle}>+ role</button>
                        {u.passkey_only && (
                          <button onClick={() => restorePassword(u)} style={smallBtnStyle}
                                  title="The owner lost their passkeys: let them sign in with their password again">
                            restore password
                          </button>
                        )}
                        {u.revoked ? (
                          <>
                            <button onClick={() => reinstateUser(u)} style={smallBtnStyle}>reinstate</button>
                            <button onClick={() => purgeAccount(u)} style={dangerBtnStyle}
                                    title="Permanently delete this revoked account">purge</button>
                          </>
                        ) : (
                          <button onClick={() => setRevoking(u)} style={dangerBtnStyle}>revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Roles" count={roles.length}>
            {roles.length === 0 ? (
              <Empty>No roles. Roles are created by the Protect action on an asset, or the access_ctl CLI.</Empty>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={trHeadStyle}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => {
                    const revoked = !!role.revoked_at;
                    const isAnon = role.name === 'anonymous';
                    return (
                      <tr key={role.id} style={trStyle}>
                        <td style={tdStyle}>{role.id}</td>
                        <td style={tdStyle}><code title={role.name}>{roleLabel(role.name)}</code></td>
                        <td style={{ ...tdStyle, color: '#666' }}>{role.description || ''}</td>
                        <td style={tdStyle}>
                          {revoked ? <span style={{ ...pillStyle, background: '#fce4e4', color: '#a33' }}>revoked</span>
                            : <span style={{ ...pillStyle, background: '#e6f5e6', color: '#363' }}>active</span>}
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          {isAnon ? (
                            <em style={{ color: '#999', fontSize: '0.75rem' }}>implicit floor — locked</em>
                          ) : (
                            <>
                              {revoked
                                ? <button onClick={() => reinstateRole(role)} style={smallBtnStyle}>reinstate</button>
                                : <button onClick={() => revokeRole(role)} style={smallBtnStyle}>revoke</button>}
                              <button onClick={() => deleteRole(role)} style={dangerBtnStyle}>delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </>
      )}

      {assigningTo && (
        <AssignModal user={assigningTo} roles={roles}
          onClose={() => setAssigningTo(null)}
          onDone={() => { setAssigningTo(null); refresh(); }} />
      )}
      {creating && (
        <CreateUserModal onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); refresh(); }} />
      )}
      {passFor && (
        <PassModal user={passFor} passDefault={passDefault}
          onClose={() => setPassFor(null)}
          onDone={() => { setPassFor(null); refresh(); }} />
      )}
      {revoking && (
        <RevokeModal user={revoking}
          onClose={() => setRevoking(null)}
          onDone={() => { setRevoking(null); refresh(); }} />
      )}
    </div>
  );
}

function LastAccess({ iso }) {
  const ago = formatLastAccess(iso);
  return (
    <>
      {formatAccessTime(iso)}
      {ago && <span style={{ color: '#888' }}> · {ago}</span>}
    </>
  );
}

function RevokedBadge({ revoked }) {
  if (!revoked) return <span style={{ ...pillStyle, background: '#e6f5e6', color: '#363' }}>active</span>;
  const label = revoked.until
    ? `revoked until ${new Date(revoked.until).toLocaleString()}`
    : 'revoked (permanent)';
  return <span style={{ ...pillStyle, background: '#fce4e4', color: '#a33' }} title={revoked.kind}>{label}</span>;
}

export function PassCell({ pass }) {
  if (!pass) return <em style={{ color: '#999' }}>none</em>;
  return (
    <span title={pass.own_limit == null ? 'the deployment default' : 'this account\'s own limit'}>
      {pass.used} / {pass.allowed} used{pass.own_limit == null ? '' : ' *'}
      {pass.revoked && <span style={{ ...pillStyle, background: '#fce4e4', color: '#a33', marginLeft: 6 }}>revoked</span>}
    </span>
  );
}

// The request PassModal sends: only what the operator changed, so an untouched field
// keeps its value on the agent. A blank limit means the deployment default (null).
export function passChanges(pass, { limit, revoked, resetUsed }) {
  const out = {};
  const own = pass ? pass.own_limit : null;
  const next = limit.trim() === '' ? null : Number(limit);
  if (next !== own) out.stations_allowed = next;
  if (revoked !== !!(pass && pass.revoked)) out.revoked = revoked;
  if (resetUsed) out.reset_used = true;
  return out;
}

function PassModal({ user, passDefault, onClose, onDone }) {
  const pass = user.station_pass;
  const [limit, setLimit] = useState(pass && pass.own_limit != null ? String(pass.own_limit) : '');
  const [revoked, setRevoked] = useState(!!(pass && pass.revoked));
  const [resetUsed, setResetUsed] = useState(false);
  const [busy, setBusy] = useState(false);
  const badLimit = limit.trim() !== '' && !/^\d+$/.test(limit.trim());

  const submit = async () => {
    if (badLimit) return;
    const body = passChanges(pass, { limit, revoked, resetUsed });
    if (pass && Object.keys(body).length === 0) { onClose(); return; }
    setBusy(true);
    const r = await fetch(`/api/user-access/accounts/${user.account_id}/station-pass`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (!r.ok) { window.alert(`station pass update failed: HTTP ${r.status}\n${await r.text()}`); return; }
    onDone();
  };

  return (
    <Modal title={`Station pass — ${user.email || user.account_id}`} onClose={onClose}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 10 }}>
        How many demo stations this person can build with "Build on Ave Q".
        {pass ? ` Used so far: ${pass.used}.` : ' They have no pass yet; saving creates one.'}
      </div>
      <label style={radioRowStyle}>
        Limit
        <input value={limit} onChange={(e) => setLimit(e.target.value)} inputMode="numeric"
          placeholder={passDefault == null ? 'default' : `default (${passDefault})`}
          style={{ ...inputStyle, width: 120 }} data-testid="pass-limit" />
      </label>
      {badLimit && <div style={{ color: '#a33', fontSize: '0.75rem' }}>A whole number, or blank for the default.</div>}
      <label style={radioRowStyle}>
        <input type="checkbox" checked={revoked} onChange={(e) => setRevoked(e.target.checked)} />
        Revoked — can't build any more
      </label>
      <label style={radioRowStyle}>
        <input type="checkbox" checked={resetUsed} onChange={(e) => setResetUsed(e.target.checked)} />
        Reset the used count to 0
      </label>
      <div style={modalActionsStyle}>
        <button onClick={onClose} style={smallBtnStyle}>cancel</button>
        <button onClick={submit} disabled={busy || badLimit} style={primaryBtnStyle}>save</button>
      </div>
    </Modal>
  );
}

function AssignModal({ user, roles, onClose, onDone }) {
  const held = new Set((user.roles || []).map((r) => r.role_id));
  const assignable = roles.filter((r) => r.name !== 'anonymous' && !r.revoked_at && !held.has(r.id));
  const [roleId, setRoleId] = useState(assignable.length ? String(assignable[0].id) : '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!roleId) return;
    setBusy(true);
    const r = await fetch(`/api/user-access/accounts/${user.account_id}/roles`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role_id: Number(roleId) }),
    });
    setBusy(false);
    if (!r.ok) { window.alert(`assign failed: HTTP ${r.status}\n${await r.text()}`); return; }
    onDone();
  };

  return (
    <Modal title={`Assign role → ${user.email || user.account_id}`} onClose={onClose}>
      {assignable.length === 0 ? (
        <Empty>No assignable roles (all already held, revoked, or only the anonymous floor exists).</Empty>
      ) : (
        <>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} style={inputStyle}>
            {assignable.map((r) => <option key={r.id} value={r.id}>{roleLabel(r.name)}</option>)}
          </select>
          <div style={modalActionsStyle}>
            <button onClick={onClose} style={smallBtnStyle}>cancel</button>
            <button onClick={submit} disabled={busy} style={primaryBtnStyle}>assign</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ok = email.includes('@') && password.length >= 8;

  const submit = async () => {
    setBusy(true);
    setError(null);
    const r = await fetch('/api/user-access/accounts', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password, display_name: name.trim() || null }),
    });
    setBusy(false);
    if (r.status === 409) { setError('That email already has an account.'); return; }
    if (!r.ok) { setError(`HTTP ${r.status}: ${await r.text()}`); return; }
    onDone();
  };

  return (
    <Modal title="Create user" onClose={onClose}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 10 }}>
        Sign-up on aveq.io is by invitation. Share the email and password with the person; they can
        change the password from their profile after signing in.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input type="email" placeholder="Email" value={email} autoFocus data-testid="new-email"
          onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
        <input type="text" placeholder="Display name (optional)" value={name} data-testid="new-name"
          onChange={(e) => setName(e.target.value)} style={inputStyle} />
        <input type="text" placeholder="Initial password (8+ characters)" value={password} data-testid="new-password"
          onChange={(e) => setPassword(e.target.value)} style={inputStyle} autoComplete="off" />
      </div>
      {error && <div style={{ color: '#c00', fontSize: '0.8rem', marginTop: 8 }}>{error}</div>}
      <div style={modalActionsStyle}>
        <button onClick={onClose} style={smallBtnStyle}>cancel</button>
        <button onClick={submit} disabled={busy || !ok} style={primaryBtnStyle}>create</button>
      </div>
    </Modal>
  );
}

function RevokeModal({ user, onClose, onDone }) {
  const [kind, setKind] = useState('temporary');
  const [until, setUntil] = useState('');  // datetime-local string
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const payload = { kind };
    if (kind === 'temporary') {
      if (!until) { window.alert('Pick an "until" date/time for a temporary revoke.'); return; }
      payload.until = new Date(until).toISOString();
    }
    setBusy(true);
    const r = await fetch(`/api/user-access/accounts/${user.account_id}/revoke`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (!r.ok) { window.alert(`revoke failed: HTTP ${r.status}\n${await r.text()}`); return; }
    onDone();
  };

  return (
    <Modal title={`Revoke ${user.email || user.account_id}`} onClose={onClose}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 10 }}>
        Blocks login + kills live sessions. The user falls back to public/anonymous access until reinstated.
      </div>
      <label style={radioRowStyle}>
        <input type="radio" checked={kind === 'temporary'} onChange={() => setKind('temporary')} />
        Temporary — auto-reinstates at:
      </label>
      <input type="datetime-local" value={until} disabled={kind !== 'temporary'}
        onChange={(e) => setUntil(e.target.value)} style={{ ...inputStyle, marginLeft: 22 }} />
      <label style={radioRowStyle}>
        <input type="radio" checked={kind === 'permanent'} onChange={() => setKind('permanent')} />
        Permanent — until manually reinstated.
      </label>
      <div style={modalActionsStyle}>
        <button onClick={onClose} style={smallBtnStyle}>cancel</button>
        <button onClick={submit} disabled={busy} style={dangerBtnStyle}>revoke</button>
      </div>
    </Modal>
  );
}

// ---- shared bits ----------------------------------------------------

function Section({ title, count, action, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{title}</h3>
        <span style={{ color: '#aaa', fontSize: '0.8rem' }}>({count})</span>
        <div style={{ flex: 1 }} />
        {action}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>{children}</div>;
}

function Modal({ title, onClose, children }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ ...smallBtnStyle, marginRight: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---- styles ---------------------------------------------------------

const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' };
const trHeadStyle = { borderBottom: '2px solid #e0e0e0' };
const trStyle = { borderBottom: '1px solid #f0f0f0' };
const thStyle = { textAlign: 'left', padding: '6px 10px', color: '#666', fontWeight: 600 };
const tdStyle = { padding: '6px 10px', verticalAlign: 'top' };
const badgeStyle = { background: '#eef', border: '1px solid #dde', borderRadius: 3, padding: '1px 6px', fontSize: '0.75rem' };
const pillStyle = { borderRadius: 10, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 };
const refreshBtnStyle = { background: 'none', border: '1px solid #ccc', borderRadius: 3, padding: '2px 8px', fontSize: '0.75rem', color: '#666', cursor: 'pointer' };
const smallBtnStyle = { background: 'none', border: '1px solid #ccc', borderRadius: 3, padding: '2px 8px', fontSize: '0.75rem', color: '#555', cursor: 'pointer', marginRight: 6 };
const dangerBtnStyle = { ...smallBtnStyle, color: '#a33', borderColor: '#e0b0b0' };
// admin-button-color: same green as "+ Add Network" (NetworksView buttonPrimaryStyle).
const primaryBtnStyle = { background: '#5fa67a', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: '0.85rem', color: '#fff', cursor: 'pointer' };
const revokeBtnStyle = { background: 'none', border: 'none', color: '#a33', cursor: 'pointer', fontSize: '0.8rem', padding: '0 2px' };
const inputStyle = { padding: '4px 8px', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: 3 };
const radioRowStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', margin: '8px 0 2px' };
const modalActionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modalStyle = { background: '#fff', borderRadius: 6, padding: '20px 22px', width: 420, maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' };
